import { ref } from 'vue';

/**
 * Global state for surfacing LIMIT_EXCEEDED errors from server-side send flows.
 *
 * Edge functions (send-email, send-sms, process-evaluation) return:
 *   status: 403
 *   body: { error, upgrade: true, details: { planId, currentUsage, limit, usageKey } }
 *
 * Callers detect these via `parseLimitError()` and surface them with `triggerLimitError()`.
 * A single <LimitErrorBanner /> mounted in AppLayout watches this state and renders
 * the existing UpgradeModal.
 */

const limitError = ref(null);

const USAGE_KEY_TO_RESOURCE = {
  emails_sent: 'emails_sent',
  sms_sent: 'sms_sent',
  evaluation_ocr_used: 'evaluation_ocr',
};

/**
 * Inspect an error from supabase.functions.invoke or fetch and return limit details if applicable.
 * @returns {{ resource: string, currentUsage: number, limit: number } | null}
 */
export async function parseLimitError(error) {
  if (!error) return null;

  let body = null;
  try {
    if (typeof error.context?.json === 'function') {
      body = await error.context.json();
    } else if (error.context?.body) {
      body = typeof error.context.body === 'string' ? JSON.parse(error.context.body) : error.context.body;
    }
  } catch {
    // ignore
  }

  if (body?.upgrade && body?.details) {
    const usageKey = body.details.usageKey || body.details.usage_key || 'emails_sent';
    return {
      resource: USAGE_KEY_TO_RESOURCE[usageKey] || usageKey,
      currentUsage: body.details.currentUsage ?? body.details.current_usage ?? 0,
      limit: body.details.limit ?? 0,
    };
  }

  // Fallback for plain `Error` shape (notifications.js direct fetch)
  if (typeof error?.message === 'string' && /usage limit reached:\s*(\w+)/i.test(error.message)) {
    const match = error.message.match(/usage limit reached:\s*(\w+)\s*\((\d+)\/(-?\d+)\)/i);
    if (match) {
      return {
        resource: USAGE_KEY_TO_RESOURCE[match[1]] || match[1],
        currentUsage: parseInt(match[2], 10),
        limit: parseInt(match[3], 10),
      };
    }
  }

  return null;
}

export function triggerLimitError(details) {
  if (!details) return;
  limitError.value = details;
}

export function clearLimitError() {
  limitError.value = null;
}

export function useLimitErrors() {
  return { limitError, parseLimitError, triggerLimitError, clearLimitError };
}
